import { useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Zap,
  AlertCircle,
  DollarSign,
} from 'lucide-react';
import { TaxSuggestions } from '../store/analysisSlice';

interface TaxSuggestionsPanelProps {
  suggestions: TaxSuggestions;
}

export default function TaxSuggestionsPanel({ suggestions }: TaxSuggestionsPanelProps) {
  const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());

  const toggleCompleted = (index: number) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
    }
    setCompletedItems(newCompleted);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
        <p className="text-xs text-amber-800">
          These suggestions are AI-generated tax planning considerations, not professional tax or investment advice.
        </p>
      </div>

      {/* Filing Recommendation */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 mb-1">
              Filing Recommendation
            </h3>
            <p className="text-blue-800 mb-3">
              {suggestions.filingRecommendation.status}
            </p>
            {suggestions.filingRecommendation.estimatedSavings > 0 && (
              <div className="flex items-baseline space-x-1">
                <span className="text-sm text-blue-700">Potential savings:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {formatCurrency(suggestions.filingRecommendation.estimatedSavings)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Deduction Comparison */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          <span>Deduction Comparison</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Standard Deduction */}
          <div className={`rounded-lg p-4 border-2 ${
            suggestions.deductionComparison.recommended === 'standard'
              ? 'border-green-500 bg-green-50'
              : 'border-gray-200 bg-gray-50'
          }`}>
            <p className="text-sm font-medium text-gray-600 mb-2">Standard Deduction</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(suggestions.deductionComparison.standard)}
            </p>
            {suggestions.deductionComparison.recommended === 'standard' && (
              <div className="mt-2 flex items-center space-x-1 text-green-700 text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                <span>Recommended</span>
              </div>
            )}
          </div>

          {/* Itemized Deductions */}
          <div className={`rounded-lg p-4 border-2 ${
            suggestions.deductionComparison.recommended === 'itemized'
              ? 'border-green-500 bg-green-50'
              : 'border-gray-200 bg-gray-50'
          }`}>
            <p className="text-sm font-medium text-gray-600 mb-2">Itemized Deductions</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(suggestions.deductionComparison.itemized)}
            </p>
            {suggestions.deductionComparison.recommended === 'itemized' && (
              <div className="mt-2 flex items-center space-x-1 text-green-700 text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                <span>Recommended</span>
              </div>
            )}
          </div>
        </div>

        {/* Savings Highlight */}
        {suggestions.deductionComparison.savings > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <Zap className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-900">Additional Savings</p>
              <p className="text-xl font-bold text-green-700">
                {formatCurrency(suggestions.deductionComparison.savings)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Credits Identified */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          <span>Tax Credits Available</span>
        </h3>

        <div className="space-y-3">
          {suggestions.creditsList.length > 0 ? (
            suggestions.creditsList.map((credit, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{credit.name}</h4>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(credit.value)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{credit.description}</p>
              </div>
            ))
          ) : (
            <p className="text-gray-600">No credits identified at this time.</p>
          )}
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          <span>Action Items</span>
        </h3>

        <div className="space-y-3">
          {suggestions.actionItems.length > 0 ? (
            suggestions.actionItems.map((item, idx) => (
              <div
                key={idx}
                className={`rounded-lg p-4 border-l-4 cursor-pointer transition-all ${
                  completedItems.has(idx)
                    ? 'bg-gray-50 border-l-gray-300'
                    : item.priority === 'high'
                    ? 'bg-red-50 border-l-red-500'
                    : item.priority === 'medium'
                    ? 'bg-yellow-50 border-l-yellow-500'
                    : 'bg-blue-50 border-l-blue-500'
                }`}
                onClick={() => toggleCompleted(idx)}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={completedItems.has(idx)}
                    onChange={() => toggleCompleted(idx)}
                    className="mt-1 w-5 h-5 rounded cursor-pointer accent-blue-600"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1">
                    <h4 className={`font-semibold mb-1 ${
                      completedItems.has(idx) ? 'text-gray-500 line-through' : 'text-gray-900'
                    }`}>
                      {item.title}
                    </h4>
                    <p className={`text-sm ${
                      completedItems.has(idx) ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {item.description}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                    item.priority === 'high'
                      ? 'bg-red-100 text-red-700'
                      : item.priority === 'medium'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-600">No action items at this time.</p>
          )}
        </div>
      </div>

      {/* Risk Flags */}
      {suggestions.riskItems.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span>Alerts & Considerations</span>
          </h3>

          <div className="space-y-3">
            {suggestions.riskItems.map((item, idx) => (
              <div
                key={idx}
                className={`rounded-lg p-4 flex items-start space-x-3 ${
                  item.severity === 'error'
                    ? 'bg-red-50 border border-red-200'
                    : item.severity === 'warning'
                    ? 'bg-yellow-50 border border-yellow-200'
                    : 'bg-blue-50 border border-blue-200'
                }`}
              >
                <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  item.severity === 'error'
                    ? 'text-red-600'
                    : item.severity === 'warning'
                    ? 'text-yellow-600'
                    : 'text-blue-600'
                }`} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    item.severity === 'error'
                      ? 'text-red-900'
                      : item.severity === 'warning'
                      ? 'text-yellow-900'
                      : 'text-blue-900'
                  }`}>
                    {item.type}
                  </p>
                  <p className={`text-sm mt-1 ${
                    item.severity === 'error'
                      ? 'text-red-800'
                      : item.severity === 'warning'
                      ? 'text-yellow-800'
                      : 'text-blue-800'
                  }`}>
                    {item.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
